import React, { useRef, useState } from "react";
import { calculateDoughnutData } from "./calculateDoughnutData";
import { useFilteredSalesData } from "./hooks/useFilteredSalesData";
import { useStoreOptions } from "./hooks/useStoreOptions";
import { upsertUseCase } from "../../../../usecases/object";
import Header from "./components/Header";
import Charts from "./components/Charts";
import PaymentSummary from "./components/PaymentSummary";

// Constants
const CHART_CONTAINER_ID = "chart-container10";

const ImageModal = ({ src, onClose }) => (
  <div
    className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
    style={{ backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1100 }}
    onClick={onClose}
  >
    <img
      src={src}
      style={{ maxWidth: "90%", maxHeight: "90%", cursor: "zoom-out" }}
      onClick={(e) => e.stopPropagation()}
      alt="Zoomed view"
    />
  </div>
);

const DeleteConfirmationModal = ({ onConfirm, onCancel }) => (
  <div
    className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
    style={{ zIndex: 1100 }}
  >
    <div
      className="bg-white p-4 rounded shadow"
      style={{ maxWidth: 320, textAlign: "center" }}
    >
      <p>Are you sure you want to delete this image?</p>
      <div className="d-flex justify-content-around mt-3">
        <button className="btn btn-danger" onClick={onConfirm}>
          Yes, delete
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

function ShareSalesContribution({ objects, storess }) {
  // State management
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedOption, setSelectedOption] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [paymentUpload, setPaymentUpload] = useState([]);
  const [viewBucket, setViewBucket] = useState(null);
  const [zoomSrc, setZoomSrc] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({
    show: false,
    url: null,
    bucket: null,
  });

  const chartRef = useRef();

  // Custom hooks
  const { filteredData } = useFilteredSalesData(
    objects,
    selectedOption,
    startDate,
    endDate,
  );
  const storeOptions = useStoreOptions(objects);
  const doughnutData = calculateDoughnutData(filteredData);

  // Event handlers
  const handlePaymentDateChange = (e) => {
    const dateStr = e.target.value;
    setPaymentDate(dateStr);

    const [year, month, day] = dateStr.split("-").map(Number);
    const start = new Date(year, month - 1, day, 5, 0, 0);
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);

    const onlyThatShift = filteredData.filter((tx) => {
      const txDate = new Date(tx.tran_date.replace(" ", "T"));
      return txDate >= start && txDate < end;
    });

    const summary = calculateDoughnutData(onlyThatShift)[2];
    const groupedWithTxns = summary.map((item) => ({
      ...item,
      transactions: onlyThatShift.filter((tx) => tx.paymenttype === item.name),
    }));

    setPaymentUpload(groupedWithTxns);
  };

  const handleFileChange = async (value, bucket) => {
    const newUrls = Array.isArray(value) ? value : [value];
    const txns = bucket.transactions || [];

    for (const tx of txns) {
      const existing = Array.isArray(tx.shareArray) ? tx.shareArray : [];
      const shareArray = [...existing, ...newUrls];
      const payload = { ...tx, shareArray };
      delete payload.storeCodePart;

      await upsertUseCase().execute("transactions", payload);
    }
  };

  const handleDeleteRequest = (url, bucket) => {
    setConfirmDelete({ show: true, url, bucket });
  };

  const handleConfirmDelete = async () => {
    const { url, bucket } = confirmDelete;
    const txns = bucket.transactions || [];

    for (const tx of txns) {
      const old = Array.isArray(tx.shareArray) ? tx.shareArray : [];
      const updated = old.filter((u) => u !== url);

      const payload = { ...tx, shareArray: updated };
      delete payload.storeCodePart;

      await upsertUseCase().execute("transactions", payload);
    }

    const newBucket = {
      ...bucket,
      transactions: bucket.transactions.map((tx) => ({
        ...tx,
        shareArray: (tx.shareArray || []).filter((u) => u !== url),
      })),
    };

    setViewBucket(newBucket);
    setPaymentUpload((prev) =>
      prev.map((b) => (b.name === bucket.name ? newBucket : b)),
    );
    setConfirmDelete({ show: false, url: null, bucket: null });
  };

  const handleCancelDelete = () =>
    setConfirmDelete({ show: false, url: null, bucket: null });

  return (
    <div className="">
      <Header
        filteredData={filteredData}
        storess={storess}
        chartContainerId={CHART_CONTAINER_ID}
      />

      <Charts
        chartRef={chartRef}
        chartContainerId={CHART_CONTAINER_ID}
        doughnutData={doughnutData}
      />

      <PaymentSummary
        paymentDate={paymentDate}
        filteredData={filteredData}
        paymentUpload={paymentUpload}
        handlePaymentDateChange={handlePaymentDateChange}
        handleFileChange={handleFileChange}
        setViewBucket={setViewBucket}
        viewBucket={viewBucket}
        handleDeleteRequest={handleDeleteRequest}
        setZoomSrc={setZoomSrc}
      />

      {confirmDelete.show && (
        <DeleteConfirmationModal
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}

      {zoomSrc && <ImageModal src={zoomSrc} onClose={() => setZoomSrc(null)} />}
    </div>
  );
}

export default ShareSalesContribution;
